import { createApi } from '@reduxjs/toolkit/query/react';

import {
  ClientsEndpoints as Endpoints,
  type DeleteClientsBySearchIdRequest,
  type DeleteClientsBySearchIdResponse,
  type DeleteClientsRequest,
  type DeleteClientsResponse,
  type ExportClientsToExcelByIdsRequest,
  type ExportClientsToExcelByIdsResponse,
  type ExportClientsToExcelRequest,
  type ExportClientsToExcelResponse,
  type FindClientsByPhoneRequest,
  type FindClientsByPhoneResponse,
  type GetClientsFilterDataRequest,
  type GetClientsFilterDataResponse,
  type GetClientsMessagesRequest,
  type GetClientsMessagesResponse,
  type GetClientsSearchResultRequest,
  type GetClientsSearchResultResponse,
  type ImportExcelRequest,
  type ImportExcelResponse,
  type SearchClientsByIntegrationRequest,
  type SearchClientsByIntegrationResponse,
  type SearchClientsForRecordRequest,
  type SearchClientsForRecordResponse,
  type SearchClientsRequest,
  type SearchClientsResponse,
  type StopImportExcelRequest,
  type StopImportExcelResponse,
} from '@app/api/Clients/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import { CLIENTS_CHUNK } from '@app/constants';

export const clientsApi3 = createApi({
  reducerPath: 'clientsApi3',
  baseQuery: createApiBaseQuery(CLIENTS_CHUNK),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.DeleteClientsBySearchId]: mutation<
      DeleteClientsBySearchIdResponse,
      DeleteClientsBySearchIdRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteClientsBySearchId,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.StopImportExcel]: mutation<
      StopImportExcelResponse,
      StopImportExcelRequest
    >({
      query: (params) => ({
        url: Endpoints.StopImportExcel,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.FindClientsByPhone]: query<
      FindClientsByPhoneResponse,
      FindClientsByPhoneRequest
    >({
      query: (params) => ({
        url: Endpoints.FindClientsByPhone,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetClientsFilterData]: query<
      GetClientsFilterDataResponse,
      GetClientsFilterDataRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientsFilterData,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchClients]: mutation<
      SearchClientsResponse,
      SearchClientsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchClients,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SearchClientsForRecord]: query<
      SearchClientsForRecordResponse,
      SearchClientsForRecordRequest
    >({
      query: (params) => ({
        url: Endpoints.SearchClientsForRecord,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetClientsSearchResult]: query<
      GetClientsSearchResultResponse,
      GetClientsSearchResultRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientsSearchResult,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportClientsToExcel]: query<
      ExportClientsToExcelResponse,
      ExportClientsToExcelRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportClientsToExcel,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportClientsToExcelByIds]: mutation<
      ExportClientsToExcelByIdsResponse,
      ExportClientsToExcelByIdsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.ExportClientsToExcelByIds,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteClients]: mutation<
      DeleteClientsResponse,
      DeleteClientsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteClients,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetClientsMessages]: query<
      GetClientsMessagesResponse,
      GetClientsMessagesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientsMessages,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchClientsByIntegration]: query<
      SearchClientsByIntegrationResponse,
      SearchClientsByIntegrationRequest
    >({
      query: (params) => ({
        url: Endpoints.SearchClientsByIntegration,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ImportExcel]: mutation<ImportExcelResponse, ImportExcelRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.ImportExcel,
        method: Methods.POST,
        params,
        data,
      }),
    }),
  }),
});

export const {
  useDeleteClientsBySearchIdMutation,
  useStopImportExcelMutation,
  useFindClientsByPhoneQuery,
  useLazyFindClientsByPhoneQuery,
  useGetClientsFilterDataQuery,
  useLazyGetClientsFilterDataQuery,
  useSearchClientsMutation,
  useSearchClientsForRecordQuery,
  useLazySearchClientsForRecordQuery,
  useGetClientsSearchResultQuery,
  useLazyGetClientsSearchResultQuery,
  useExportClientsToExcelQuery,
  useLazyExportClientsToExcelQuery,
  useExportClientsToExcelByIdsMutation,
  useDeleteClientsMutation,
  useGetClientsMessagesQuery,
  useLazyGetClientsMessagesQuery,
  useSearchClientsByIntegrationQuery,
  useLazySearchClientsByIntegrationQuery,
  useImportExcelMutation,
} = clientsApi3;
